import { Link } from "react-router-dom";
import { ArrowRight, ArrowDown } from "lucide-react";
import Logo from "../components/Logo";
import { ProductPreview, Testimonials, Pricing, Eyebrow } from "../components/LandingSections";

const PROBLEMS = [
  { n: "01", title: "The Vision Gap", body: "Organizations suffer vision fade when vision is only cast once a year." },
  { n: "02", title: "The Execution Trap", body: "Consistency is exhausting. Leaders don't have time to constantly repackage their thoughts." },
  { n: "03", title: "The Generic AI Failure", body: "Basic AI tools generate vanilla content in a vacuum — they don't know your values or sound like you." },
];

const PILLARS = [
  { tag: "Pillar I", title: "The Company Profile", body: "Preload your mission, vision, and values to create a strategic boundary." },
  { tag: "Pillar II", title: "The Personal Profile", body: "Feed examples of your voice so the output always sounds like you." },
  { tag: "Pillar III", title: "The Backdrop Generator", body: "Drop raw daily musings in, and VizionCast pressures them against your profiles to create a strategic content rhythm." },
];

const OUTCOMES = [
  { icon: "👥", title: "Your Team Feels Aligned" },
  { icon: "🤝", title: "Your Customers Feel Connected" },
  { icon: "🏢", title: "Your Company Feels Scalable" },
];

const PrimaryCta = ({ children, testid }) => (
  <Link to="/register" data-testid={testid}
    className="group inline-flex items-center gap-3 bg-[#0033FF] text-white px-7 py-4 text-sm font-bold uppercase tracking-wider hover:bg-white hover:text-[#0A0A0B] transition-colors duration-300">
    {children}
    <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />
  </Link>
);

function TopBar() {
  return (
    <header className="fixed top-0 inset-x-0 z-40 bg-[#0A0A0B]/70 backdrop-blur-xl border-b border-white/10" data-testid="landing-topbar">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 h-16 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2" data-testid="landing-logo">
          <Logo size={28} /><span className="font-black tracking-tighter text-white">VIZIONCAST</span>
        </Link>
        <div className="flex items-center gap-2 sm:gap-5">
          <a href="#pricing" data-testid="landing-pricing-link" className="hidden sm:inline text-sm font-medium text-zinc-300 hover:text-white transition-colors px-2">Pricing</a>
          <Link to="/login" data-testid="landing-signin-link" className="text-sm font-medium text-zinc-300 hover:text-white transition-colors px-2">Sign in</Link>
          <Link to="/register" data-testid="landing-topbar-signup" className="bg-white text-[#0A0A0B] px-4 py-2 text-xs font-bold uppercase tracking-wider hover:bg-[#0033FF] hover:text-white transition-colors">Get started</Link>
        </div>
      </div>
    </header>
  );
}

function Hero() {
  return (
    <section className="relative min-h-[100svh] flex items-center pt-16 overflow-hidden" data-testid="landing-hero">
      <div className="absolute inset-0 landing-grid opacity-[0.07]" />
      <div className="absolute -right-40 top-1/4 w-[38rem] h-[38rem] rounded-full bg-[#0033FF] blur-[160px] opacity-30" />
      <div className="relative max-w-7xl mx-auto px-5 sm:px-8 py-20 w-full">
        <h1 className="landing-rise text-5xl sm:text-7xl lg:text-8xl font-black tracking-tighter leading-[0.92] text-white max-w-5xl" style={{ animationDelay: "120ms" }} data-testid="landing-hero-headline">
          Cast your vision.<br /><span className="text-[#4D6BFF]">Consistently.</span>
        </h1>
        <p className="landing-rise mt-8 text-base sm:text-lg text-zinc-400 max-w-2xl leading-relaxed" style={{ animationDelay: "240ms" }} data-testid="landing-hero-subheadline">
          One platform where the messy daily musings of a leader become the steady, on-brand rhythm of content your company, customers, and team can feel—anchored permanently against your core organizational strategy.
        </p>
        <div className="landing-rise mt-10 flex flex-wrap items-center gap-6" style={{ animationDelay: "360ms" }}>
          <PrimaryCta testid="landing-hero-cta">Start Casting Your Vision</PrimaryCta>
          <a href="#problem" className="text-sm text-zinc-500 hover:text-white inline-flex items-center gap-2 transition-colors" data-testid="landing-scroll-link">
            See how it works <ArrowDown className="w-4 h-4" />
          </a>
        </div>
      </div>
    </section>
  );
}

function Problem() {
  return (
    <section id="problem" className="border-t border-white/10 py-24 sm:py-32" data-testid="landing-problem">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 grid lg:grid-cols-12 gap-12">
        <div className="lg:col-span-4">
          <Eyebrow>01 — Problem</Eyebrow>
          <h2 className="text-4xl sm:text-5xl font-black tracking-tighter text-white leading-none">The Friction of Consistency</h2>
        </div>
        <div className="lg:col-span-8 divide-y divide-white/10 border-y border-white/10">
          {PROBLEMS.map((p) => (
            <div key={p.n} className="group grid sm:grid-cols-12 gap-3 py-8" data-testid={`landing-problem-${p.n}`}>
              <div className="sm:col-span-2 font-mono text-sm text-[#FF3B30]">{p.n}</div>
              <div className="sm:col-span-10">
                <h3 className="text-xl sm:text-2xl font-bold text-white mb-2 group-hover:text-[#4D6BFF] transition-colors">{p.title}</h3>
                <p className="text-zinc-400 leading-relaxed">{p.body}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function Mechanism() {
  return (
    <section className="py-24 sm:py-32 bg-[#0F0F12] border-t border-white/10" data-testid="landing-mechanism">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <Eyebrow>02 — Mechanism</Eyebrow>
        <h2 className="text-4xl sm:text-5xl font-black tracking-tighter text-white leading-none max-w-3xl mb-16">How We Turn Chaos Into Alignment</h2>
        <div className="grid md:grid-cols-3 gap-px bg-white/10 border border-white/10">
          {PILLARS.map((p, i) => (
            <div key={p.title} className="bg-[#0F0F12] p-8 sm:p-10 hover:bg-[#0033FF] transition-colors duration-500 group" data-testid={`landing-pillar-${i}`}>
              <div className="text-[11px] tracking-[0.3em] uppercase font-bold text-[#4D6BFF] group-hover:text-white/80 mb-10">{p.tag}</div>
              <h3 className="text-2xl font-black tracking-tight text-white mb-4">{p.title}</h3>
              <p className="text-zinc-400 group-hover:text-white/90 leading-relaxed transition-colors">{p.body}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function Transformation() {
  return (
    <section className="py-24 sm:py-32 border-t border-white/10" data-testid="landing-transformation">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <Eyebrow>03 — Transformation</Eyebrow>
        <h2 className="text-4xl sm:text-5xl font-black tracking-tighter text-white leading-none mb-16">What Changes</h2>
        <div className="grid md:grid-cols-3 gap-10 md:gap-8">
          {OUTCOMES.map((o, i) => (
            <div key={o.title} className="border-t-2 border-[#0033FF] pt-8 pb-2 hover:-translate-y-1 transition-transform duration-300" data-testid={`landing-outcome-${i}`}>
              <div className="text-5xl mb-6" aria-hidden="true">{o.icon}</div>
              <h3 className="text-2xl font-black tracking-tight text-white">{o.title}</h3>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function FinalCta() {
  return (
    <section className="relative overflow-hidden bg-[#0033FF] py-24 sm:py-32" data-testid="landing-final-cta">
      <div className="absolute inset-0 landing-grid opacity-10" />
      <div className="relative max-w-7xl mx-auto px-5 sm:px-8">
        <h2 className="text-4xl sm:text-6xl font-black tracking-tighter text-white leading-[0.95] mb-6 max-w-4xl" data-testid="landing-final-headline">Stop letting your vision fade into the daily noise.</h2>
        <p className="text-base sm:text-lg text-white/80 max-w-2xl mb-10">Turn your raw insights into an unshakeable organizational heartbeat today.</p>
        <Link to="/register" data-testid="landing-final-cta-btn"
          className="group inline-flex items-center gap-3 bg-[#0A0A0B] text-white px-7 py-4 text-sm font-bold uppercase tracking-wider hover:bg-white hover:text-[#0A0A0B] transition-colors duration-300">
          Get Started for Free <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />
        </Link>
      </div>
    </section>
  );
}

export default function Landing() {
  return (
    <div className="min-h-screen bg-[#0A0A0B] text-white selection:bg-[#0033FF] selection:text-white" data-testid="landing-page">
      <TopBar />
      <Hero />
      <Problem />
      <Mechanism />
      <ProductPreview />
      <Transformation />
      <Testimonials />
      <Pricing />
      <FinalCta />
      <footer className="border-t border-white/10 py-8" data-testid="landing-footer">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 flex flex-wrap items-center justify-between gap-4 text-xs text-zinc-500">
          <div className="flex items-center gap-2"><Logo size={20} /><span className="font-bold tracking-tighter text-zinc-300">VIZIONCAST</span></div>
          <div>© {new Date().getFullYear()} VizionCast. Cast your vision. Consistently.</div>
        </div>
      </footer>
    </div>
  );
}
